/* eslint-disable @typescript-eslint/no-unused-vars, eqeqeq */
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import CheckboxAsRadio from "shared/Checkbox/CheckboxAsRadio";
import { useDispatch, useSelector } from "react-redux";

import { setStayMealTypes } from "store/actions/stayMealTypes";
import { setStayCurrentPrice } from "store/actions/stayCurrentPrice";
import { setStayCurrentCharges } from "store/actions/stayCurrentCharges";
import { setStayCurrentNON } from "store/actions/stayCurrentNON";
import { setStayCurrentTotalPrice } from "store/actions/stayCurrentTotalPrice";
import { setStayCurrentTotalPriceWithCharges } from "store/actions/stayCurrentTotalPriceWithCharges";
import { setStay } from "store/actions/stay";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const WEB_HOTELIER: Record<number, string> = {
  0: "No meals included",
  1: "All inclusive",
  3: "Bed & breakfast",
  4: "Breakfast",
  10: "Full board",
  12: "Half board",
  14: "Room only",
};

const RATE_HAWK: Record<string, string> = {
  nomeal: "No meals included",
  breakfast: "Breakfast",
  "breakfast-buffet": "Breakfast",
  "continental-breakfast": "Breakfast",
  "english-breakfast": "Breakfast",
  "american-breakfast": "Breakfast",
  "asian-breakfast": "Breakfast",
  "chinese-breakfast": "Breakfast",
  "israeli-breakfast": "Breakfast",
  "japanese-breakfast": "Breakfast",
  "scandinavian-breakfast": "Breakfast",
  "scottish-breakfast": "Breakfast",
  dinner: "Dinner",
  lunch: "Lunch",
  "half-board": "Half board",
  "half-board-lunch": "Half board",
  "half-board-dinner": "Half board",
  "full-board": "Full board",
  "all-inclusive": "All inclusive",
  "super-all-inclusive": "All inclusive",
  "soft-all-inclusive": "All inclusive",
  "ultra-all-inclusive": "All inclusive",
};

// fallback prettifier for unknown RH keys (“my-custom-meal” → “My Custom Meal”)
const prettify = (k: string) =>
  k
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

// 🩹 PATCH: unified board normalization
const normalizeBoard = (src: string | undefined, board: unknown): string => {
  if (board == null || board === "") return "No meals included";

  const source = src?.toLowerCase();
  const numeric = !isNaN(Number(board));

  if (source === "webhotelier" || source === "wh" || numeric) {
    const key = Number(board);
    return WEB_HOTELIER[key] ?? "No meals included";
  }

  const key = String(board).trim().toLowerCase();
  return RATE_HAWK[key] ?? prettify(key);
};

/* ---------- same bucket logic as CancellationInput ---------------- */

const classifyCancellation = (e: string | null | undefined): 0 | 2 =>
  e === null || e === "" || e === undefined ? 0 : 2; // 0 = strict
const cancellationBucket = (e: string | null | undefined) =>
  classifyCancellation(e) === 0 ? null : "flexible";

const matchesBucket = (rate: any, bucketId: string | null) =>
  bucketId === null
    ? rate.cancellation_expiry === null
    : rate.cancellation_expiry !== null;

/* ------------------------------------------------------------------ */

const withMarkup = (n: number, markup?: number) =>
  markup && markup > 0 ? n * (1 + markup / 100) : n;

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

type Props = {
  fieldClassName?: string;
  className?: string;
  anchorDirection?: "left" | "right";
  isDisabled?: boolean;
  setSelectedMeals: (v: string) => void;
  data?: any; // hotel (for .markup)
};

const ExperiencesMealInput: FC<Props> = ({
  fieldClassName = "nc-hero-field-padding",
  className = "",
  anchorDirection,
  isDisabled = false,
  setSelectedMeals,
  data,
}) => {
  const dispatch = useDispatch();

  const stay = useSelector((s: any) => s.stay.stay) ?? [];
  const mealTypesRedux =
    useSelector((s: any) => s.stayMealTypes.stayMealTypes) ?? [];
  const cancellationTypes =
    useSelector((s: any) => s.stayCancellationTypes.stayCancellationTypes) ??
    [];

  const selectedCancellationId =
    cancellationTypes.find((c: any) => c.checked)?.cancellationId ?? null;

  /* build board list -------------------------------------------------- */
  useEffect(() => {
    const boards: any[] = [];

    stay.forEach((rate: any) => {
      if (rate.no_availability) return;

      const n = normalizeBoard(rate.src || data?.primary_API, rate.board);

      if (!boards.some((b) => b.boardId === rate.board)) {
        boards.push({
          id: rate.id,
          name: n,
          boardId: rate.board,
          description: "",
          checked: false,
        });
      }
    });

    if (!boards.length) {
      boards.push({
        id: 1,
        name: "No meals included",
        boardId: 0,
        description: "",
        checked: true,
      });
    } else {
      boards[0].checked = true;
    }

    dispatch(setStayMealTypes(boards));
    setSelectedMeals(boards[0].name);

    const firstRate = stay.find((r: any) => r.checked) ?? stay[0];
    if (firstRate) pushPrices(firstRate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stay.length]);

  // initialize the visible button label to match
  const [selectedValue, setSelectedValue] = useState<string>(() => {
    const init = mealTypesRedux.find((m: any) => m.checked)?.name;
    return init ?? "No meals included";
  });

  /* which boards are valid for current cancellation bucket? ------------ */
  const validBoardIds: Set<any> = useMemo(() => {
    const ids = stay
      .filter(
        (r: any) =>
          !r.no_availability && matchesBucket(r, selectedCancellationId as any)
      )
      .map((r: any) => r.board);
    return new Set(ids);
  }, [stay, selectedCancellationId]);

  /* push prices -------------------------------------------------------- */
  const pushPrices = (rate: any) => {
    const markup = data?.markup;
    dispatch(setStayCurrentPrice(withMarkup(rate.price, markup)));

    const rawCharges =
      (rate.totalPriceWithCharges ?? 0) - (rate.totalPrice ?? 0);
    dispatch(setStayCurrentCharges(withMarkup(rawCharges, markup)));
    dispatch(setStayCurrentNON(rate.numberOfNights));
    dispatch(setStayCurrentTotalPrice(withMarkup(rate.totalPrice, markup)));
    dispatch(
      setStayCurrentTotalPriceWithCharges(
        withMarkup(rate.totalPriceWithCharges, markup)
      )
    );
  };

  /* on change ---------------------------------------------------------- */
  const handleChange = (name: string, boardId: any) => {
    setSelectedValue(name);
    setSelectedMeals(name);

    const updatedMealTypes = mealTypesRedux.map((m: any) =>
      m.boardId === boardId ? { ...m, checked: true } : { ...m, checked: false }
    );
    dispatch(setStayMealTypes(updatedMealTypes));

    /* find cheapest rate given bucket + board ----------------------- */
    const best = stay
      .filter(
        (r: any) =>
          r.board === boardId &&
          !r.no_availability &&
          matchesBucket(r, selectedCancellationId as any)
      )
      .reduce(
        (lo: any, r: any) => (lo === null || +r.price < +lo.price ? r : lo),
        null
      );

    if (!best) return;

    const marked = stay.map((r: any) =>
      r.id === best.id ? { ...r, checked: true } : { ...r, checked: false }
    );
    dispatch(setStay(marked));
    pushPrices(best);
  };

  /* render ------------------------------------------------------------- */

  return (
    <div>
      <Popover className={`flex relative ${className}`}>
        {({ open }) => (
          <>
            <div
              className={`flex-1 flex items-center cursor-pointer ${
                open ? "nc-hero-field-focused" : ""
              }`}
            >
              <Popover.Button
                className={`flex-1 flex text-left items-center ${fieldClassName} space-x-3`}
              >
                <div className="text-neutral-300 dark:text-neutral-400">
                  <i className="la la-utensils text-4xl" />
                </div>
                <div className="flex-grow">
                  <span className="block xl:text-lg font-semibold">
                    {selectedValue}
                  </span>
                  <span className="block mt-1 text-sm text-neutral-400 leading-none">
                    Meals
                  </span>
                </div>
              </Popover.Button>
            </div>

            {!isDisabled && (
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className={`absolute ${
                    anchorDirection === "left" ? "left-0" : "right-0"
                  } z-10 w-max max-w-sm bg-white dark:bg-neutral-800 top-full mt-3 py-6 px-8 rounded-3xl shadow-xl`}
                >
                  <div className="flex flex-col space-y-5">
                    {mealTypesRedux.map((item: any) => (
                      <CheckboxAsRadio
                        key={item.boardId}
                        name={item.name}
                        label={item.name}
                        value={String(item.id)}
                        checked={item.checked}
                        disabled={!validBoardIds.has(item.boardId)}
                        onChange={() => handleChange(item.name, item.boardId)}
                      />
                    ))}
                  </div>
                </Popover.Panel>
              </Transition>
            )}
          </>
        )}
      </Popover>
    </div>
  );
};

export default ExperiencesMealInput;
